import { useState } from "react";
import { uloopApi } from "api/uloop";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Layout } from "providers/Layout";
import { useAuthContext } from "providers/Auth";
import { Card } from "shared/components/Card";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";
import { IntegrationId } from "views/settings-integration/components/AddIntegrationModal/types";
import { FeedAutoAddCard } from "views/settings-integration/integrations/shared/FeedAutoAddCard";
import { FeedListingsCard } from "views/settings-integration/integrations/shared/FeedListingsCard";
import { IntegrationDetailHeader } from "views/settings-integration/integrations/shared/IntegrationDetailHeader";
import { UloopPricingModel } from "views/settings-integration/integrations/shared/UloopPricingModel";
import { invalidateFeedQueries } from "views/settings-integration/integrations/shared/useSyndicationFeed";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { UloopPricing } from "types/landlord/partner";

const QUERY_ULOOP_STATUS = ["ULOOP", "STATUS"] as const;

const propertyCountLabel = (count: number) =>
  `${count} ${count === 1 ? "property" : "properties"}`;

export const UloopView = () => {
  const navigate = useNavigate();
  const { refreshPartnerData, partner } = useAuthContext();
  const queryClient = useQueryClient();

  const {
    data: status,
    refetch: refetchStatus,
    isFetching: isFetchingStatus,
    isPlaceholderData: isStatusPlaceholder
  } = useQuery({
    queryKey: QUERY_ULOOP_STATUS,
    queryFn: uloopApi.getStatus,
    placeholderData: partner?.uloop_connected
      ? {
          connected: true,
          pricing_model: partner.uloop_pricing_model ?? "lead",
          auto_add_listings: true,
          uloop_property_count: 0
        }
      : undefined
  });

  const [confirmingFeatured, setConfirmingFeatured] = useState(false);

  const { mutate: disconnectUloop, isPending: isDisconnecting } = useMutation({
    mutationKey: ["ULOOP", "DELETE"],
    mutationFn: () => uloopApi.disconnect(),
    onSuccess: () => {
      toast.success("Uloop integration disconnected successfully");
      refreshPartnerData();
      navigate("/settings/integration");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const { mutate: updatePricing, isPending: isUpdatingPricing } = useMutation({
    mutationKey: ["ULOOP", "UPDATE"],
    mutationFn: (pricing_model: UloopPricing) =>
      uloopApi.update({ pricing_model }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_ULOOP_STATUS });
      invalidateFeedQueries(queryClient, "uloop");
      refreshPartnerData();
      setConfirmingFeatured(false);
      toast.success("Uloop pricing model updated");
    },
    onError: (err) => {
      setConfirmingFeatured(false);
      toast.error(getErrorMessage(err));
    }
  });

  const pricingModel = status?.pricing_model ?? "lead";
  const uloopPropertyCount = status?.uloop_property_count ?? 0;
  const isCountPending = isFetchingStatus || isStatusPlaceholder;

  // Switching to Featured commits $250/mo per property, so it asks first.
  const handlePricingChange = (next: UloopPricing) => {
    if (next === pricingModel) return;
    if (next === "featured") {
      refetchStatus();
      setConfirmingFeatured(true);
    } else updatePricing("lead");
  };

  return (
    <Layout
      header={
        <IntegrationDetailHeader
          id={IntegrationId.ULOOP}
          name="Uloop"
          path="/settings/integration/uloop"
          onDisconnect={() => disconnectUloop()}
          isDisconnecting={isDisconnecting}
        />
      }
    >
      <div className="space-y-6">
        <Card className="p-6">
          <UloopPricingModel
            label="Pricing model"
            value={pricingModel}
            onChange={handlePricingChange}
            disabled={isUpdatingPricing || isStatusPlaceholder}
          />
        </Card>

        <ConfirmDialog
          opened={confirmingFeatured}
          variant="primary"
          title="Enable Featured pricing?"
          description={
            <>
              Uloop will charge <b>$250 per month</b> for every property you
              send to Uloop. Today that&apos;s{" "}
              {propertyCountLabel(uloopPropertyCount)}. This fee is charged
              directly by Uloop, not Boom.
            </>
          }
          confirmButton={{
            label: "Enable Featured",
            loading: isUpdatingPricing,
            disabled: isCountPending
          }}
          cancelButton={{ label: "Cancel", disabled: isUpdatingPricing }}
          onClose={() => setConfirmingFeatured(false)}
          onConfirm={() => updatePricing("featured")}
        />

        <FeedAutoAddCard feed="uloop" feedLabel="Uloop" />
        <FeedListingsCard feed="uloop" feedLabel="Uloop" />
      </div>
    </Layout>
  );
};
